import type * as React from 'react'
import { FC, MouseEvent, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import { Undo } from '@mui/icons-material'
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  Popover,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'

import { IPSNameLogo } from '@/assets/icons'
import { APP_BAR_HEIGHT, NEUTRAL_GREY_50 } from '@/common/constants'
import { FILTER_CHIP_SX, SQUARE_ICON_BUTTON_SX } from '@/common/styles'
import { ButtonMenu, MenuOption } from '@/components/componentLibrary/ButtonMenu'
import { ICON_MAP } from '@/components/componentLibrary/Icon/Icons'
import AppsMenu from '@/components/curb/AppsMenu'
import SettingsMenu from '@/components/curb/SettingsMenu'
import { useCurrentProduct } from '@/modules/urlRouting/hooks'
import { SEGMENTS } from '@/modules/urlRouting/paths'

export interface HeaderFilterTagProps {
  text: string
  /** Hover text for the chip. Defaults to `text`, for a chip whose label is clipped. */
  tooltip?: React.ReactNode
  closeFunction?: () => void | undefined
}
export interface HeaderBarProps {
  title: string
  titleCaption?: string
  /** Status shown at the right, after the filter tags, before the buttons (e.g. an info icon). */
  info?: React.ReactNode
  /** Page buttons, shown left of the filter button (e.g. a main action of the page). */
  actions?: React.ReactNode
  downloadOptions?: MenuOption[]
  createOptions?: MenuOption[]
  publicLink?: string
  filters?: React.JSX.Element
  numberOfFilters?: number
  showLogo?: boolean
  tags?: HeaderFilterTagProps[]
  reset?: () => void
}

export const HeaderBar = ({
  title,
  titleCaption,
  info,
  actions,
  downloadOptions,
  createOptions,
  publicLink,
  filters,
  numberOfFilters,
  tags,
  showLogo,
  reset,
}: HeaderBarProps) => {
  const { t } = useTranslation()
  const currentProduct = useCurrentProduct()

  return (
    <AppBar
      color="inherit"
      position="sticky"
      elevation={0}
      sx={{
        height: APP_BAR_HEIGHT,
        backgroundColor: NEUTRAL_GREY_50,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar>
        <Stack direction="row" justifyContent="space-between" width="100%">
          {showLogo && (
            <Stack direction="row" alignItems="center">
              <Box height="40px">
                <IPSNameLogo />
              </Box>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={7} justifyContent={'space-between'}>
            <Stack direction="row" alignItems="center" gap={1.75}>
              <Typography variant="h5" component="h1">
                {title}
              </Typography>
              {titleCaption && (
                <>
                  <Box sx={{ width: '1px', height: '22px', backgroundColor: 'divider' }} />
                  <Typography variant="body1" color="text.primary" fontWeight={500}>
                    {titleCaption}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
          {/*
            `minWidth: 0` here and on the tags row below: a flex item defaults to
            `min-width: auto`, so without it the chain from the toolbar down to the chip
            refuses to shrink and one long tag pushes these buttons off the end. See
            `FILTER_CHIP_SX`, which caps the chip itself.
          */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              // Far above the `info` status's weight of 1, so this row gives up its space first.
              sx={{ minWidth: 0, flexShrink: 1000 }}
            >
              {tags &&
                tags.map((tag, index) => (
                  /*
                    The chip clips its text to `FILTER_CHIP_SX`'s cap, so the tooltip is
                    how the full value stays reachable. Always on rather than shown only
                    when the text is actually clipped: the chip's width is dynamic - it
                    shrinks as the bar fills - so a one-shot `scrollWidth > clientWidth`
                    measurement like `CollapsedTextTooltip`'s goes stale on the next
                    resize, and a misfire would hide the name entirely. With at most a
                    handful of chips, a tooltip that repeats visible text is harmless.
                  */
                  <Tooltip key={`header-tag-${index}`} title={tag.tooltip ?? tag.text}>
                    <Chip
                      size="small"
                      label={tag.text}
                      onDelete={tag.closeFunction || undefined}
                      sx={[
                        FILTER_CHIP_SX,
                        { marginTop: '4px !important', marginBottom: '4px !important' },
                      ]}
                    />
                  </Tooltip>
                ))}
            </Stack>
            {info && (
              <Typography
                component="div"
                variant="body2"
                color="text.primary"
                fontWeight={500}
                noWrap
                // Shrinks only when the tags row is empty, so the filter and download buttons
                // stay in view. A text status, such as "Updated: <date>", must be a block to
                // get an ellipsis. Other content, such as an icon, is centered with flex.
                sx={{
                  minWidth: 0,
                  display: typeof info === 'string' ? 'block' : 'flex',
                  alignItems: 'center',
                }}
              >
                {info}
              </Typography>
            )}
            {actions}
            {filters && (
              <FilterPopup numberOfFilters={numberOfFilters} filters={filters} reset={reset} />
            )}
            {createOptions && (
              <ButtonMenu
                label={t('common.create', 'Create')}
                Icon={<ICON_MAP.add />}
                options={createOptions}
                square
                portalIntoHeader
                useTooltip={false}
              />
            )}
            {downloadOptions && (
              <ButtonMenu
                label={t('common.download', 'Download')}
                Icon={<ICON_MAP.download />}
                options={downloadOptions}
                square
                portalIntoHeader
              />
            )}
            {publicLink && <PublicPageButton to={publicLink} />}
            {currentProduct !== SEGMENTS.PUBLIC && (
              <>
                <Divider sx={{ pl: '8px' }} orientation="vertical" />
                <AppsMenu />
                <SettingsMenu />
              </>
            )}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  )
}

interface FilterPopupProps {
  numberOfFilters?: number
  filters: any
  reset?: () => void | undefined
}
export const FilterPopup: FC<FilterPopupProps> = ({ filters, numberOfFilters, reset }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const hasFilters = !!numberOfFilters

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)

  const open = Boolean(anchorEl)
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton
        aria-label="filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleClick}
        sx={[
          SQUARE_ICON_BUTTON_SX,
          hasFilters && {
            backgroundColor: 'primary.main',
            borderColor: 'primary.main',
            // The icon inherits `currentColor`.
            color: 'common.white',
            '&:hover': { backgroundColor: 'primary.dark' },
          },
        ]}
      >
        <Badge
          badgeContent={numberOfFilters}
          color="primary"
          data-testid="number-of-filters-badge"
          // The badge sits on the button's own blue once filters are applied, so it
          // inverts rather than disappearing into it.
          slotProps={{ badge: { sx: { bgcolor: 'common.white', color: 'primary.main' } } }}
        >
          <ICON_MAP.filter />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableScrollLock
        // The pop-up traps focus and closes on Escape, so it is a dialog to screen readers.
        // The role also tells axe that its content needs no landmark: MUI portals it to the
        // end of `body`, outside the page's `main`.
        slotProps={{ paper: { role: 'dialog', 'aria-label': t('common.filters', 'Filters') } }}
      >
        <Stack spacing={1.5} p={1} sx={{ maxWidth: '350px' }}>
          <FormControl>
            <Stack spacing={2} p={2}>
              {/*
                Without this boundary the nearest one is the page header's own, whose fallback has
                no `filters` prop — a suspending query inside a filter panel would hide the header
                and the filter button itself until it resolved.
              */}
              <Suspense fallback={<CircularProgress size={24} sx={{ alignSelf: 'center' }} />}>
                {filters}
              </Suspense>
            </Stack>
          </FormControl>
          {!!reset && (
            <Button
              variant="text"
              onClick={reset}
              startIcon={<Undo />}
              // `primary.main` on white is 3.2:1, below the 4.5:1 that WCAG AA asks for text of
              // this size. `primary.dark` is 6:1.
              sx={{ color: 'primary.dark' }}
            >
              {t('common.resetFilters', 'Reset Filters')}
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  )
}

const PublicPageButton: FC<{ to: string }> = ({ to }) => {
  const { t } = useTranslation()
  const label = t('common.viewPublicPage', 'View public page')

  return (
    <Tooltip title={label}>
      <IconButton
        component={RouterLink}
        to={to}
        target="_blank"
        aria-label={label}
        sx={SQUARE_ICON_BUTTON_SX}
      >
        <ICON_MAP.public />
      </IconButton>
    </Tooltip>
  )
}
